<template>
    <div class="section text-center mt-2" v-if="loading">
        <div class="spinner-border text-primary" role="status"></div>
    </div>

    <template v-else-if="meeting">
        <div class="section pt-1 pb-0">
            <div class="fm-card fm-hero-card">
                <div class="fm-hero-row">
                    <div class="fm-hero-icon"><ion-icon name="people-circle-outline"></ion-icon></div>
                    <div class="fm-hero-info">
                        <div class="fm-hero-title">{{ meeting.name }}</div>
                        <div class="fm-hero-time">
                            {{ formatTime(meeting.meeting_time) }}<span v-if="meeting.place"> · {{ meeting.place }}</span>
                        </div>
                    </div>
                    <span class="fm-status" :class="statusClass(response.attendance)">{{ statusLabel(response.attendance) }}</span>
                </div>
                <div class="fm-reason-row" v-if="response.attendance === 'L' && response.remarks">
                    <ion-icon name="chatbox-ellipses-outline"></ion-icon>
                    <span>{{ response.remarks }}</span>
                </div>
            </div>
        </div>

        <div class="section pt-1 pb-0" v-if="showCertificate || meeting.has_calendar">
            <div class="fm-download-row">
                <button v-if="showCertificate" class="fm-download-btn" @click="downloadFile('certificate')">
                    <ion-icon name="ribbon-outline"></ion-icon> Certificate
                </button>
                <button v-if="meeting.has_calendar" class="fm-download-btn" @click="downloadFile('calendar')">
                    <ion-icon name="calendar-outline"></ion-icon> Calendar
                </button>
            </div>
        </div>

        <div class="section pt-1 pb-2" v-if="!hasResponded">
            <div class="fm-actions">
                <button class="fm-action-btn fm-present-btn" :disabled="saving" @click="markPresent">
                    <ion-icon name="checkmark-circle-outline"></ion-icon> Present
                </button>
                <button class="fm-action-btn fm-leave-btn" :disabled="saving" @click="showLeaveForm = !showLeaveForm">
                    <ion-icon name="close-circle-outline"></ion-icon> Leave
                </button>
            </div>

            <div class="fm-card fm-leave-card" v-if="showLeaveForm">
                <textarea
                    class="form-control fm-textarea"
                    v-model="leaveReason"
                    rows="2"
                    placeholder="Reason for leave"
                ></textarea>
                <button class="fm-submit-leave-btn" :disabled="saving || !leaveReason.trim()" @click="markLeave">
                    <span v-if="saving" class="spinner-border spinner-border-sm"></span>
                    <span v-else>Submit Leave</span>
                </button>
            </div>
        </div>
        <div class="section pt-1 pb-2" v-else>
            <div class="fm-responded-note">
                <ion-icon name="checkmark-done-outline"></ion-icon>
                <span>Your response has been recorded and can't be changed.</span>
            </div>
        </div>
    </template>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
</template>

<script>
import UserService from "../../services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "QHLSFacultyMeetDetail",
    components: {
        AlertModel,
    },
    data() {
        return {
            loading: false,
            saving: false,
            alert_type: "",
            alert_message: "",
            meeting: null,
            response: { attendance: "N", remarks: "" },
            showLeaveForm: false,
            leaveReason: "",
        };
    },
    computed: {
        // A certificate is a record of participation — only meaningful once the
        // faculty has actually marked themselves Present for the meeting.
        showCertificate() {
            return !!(this.meeting && this.meeting.has_certificate && this.response.attendance === "P");
        },
        // Present/Leave is a one-time submission — once recorded, the actions and
        // leave-reason form are hidden so it can't be changed afterward.
        hasResponded() {
            return this.response.attendance === "P" || this.response.attendance === "L";
        },
    },
    mounted() {
        if (!this.$store.state.auth.user) {
            location.replace("/");
            return;
        }
        this.$root.updateParent("Faculty Meet");
        this.getDetails();
    },
    methods: {
        statusLabel(status) {
            if (status === "P") return "Present";
            if (status === "L") return "Leave";
            return "Not Responded";
        },
        statusClass(status) {
            if (status === "P") return "fm-status-present";
            if (status === "L") return "fm-status-leave";
            return "fm-status-pending";
        },
        formatTime(iso) {
            if (!iso) return "";
            const d = new Date(iso);
            return d.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        getDetails() {
            this.loading = true;
            UserService.authPostRequest("get-qhls-faculty-meet-details", { id: this.$route.params.id }).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === "success") {
                        this.meeting = response.data.data.meeting;
                        this.response = response.data.data.response;
                    } else {
                        this.alert_type = "Warning";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    }
                },
                (error) => {
                    this.loading = false;
                    this.alert_type = "Error";
                    this.alert_message =
                        (error.response && error.response.data && error.response.data.message) ||
                        error.message ||
                        error.toString();
                    this.$refs.alert_model.openModel();
                }
            );
        },
        markPresent() {
            this.saveAttendance("P", "");
        },
        markLeave() {
            if (!this.leaveReason.trim()) return;
            this.saveAttendance("L", this.leaveReason.trim());
        },
        saveAttendance(attendance, remarks) {
            this.saving = true;
            UserService.authPostRequest("store-qhls-faculty-meet-attendance", {
                meeting_id: this.meeting.id,
                attendance: attendance,
                remarks: remarks,
            }).then(
                (response) => {
                    this.saving = false;
                    if (response.data.status === "success") {
                        this.response.attendance = attendance;
                        this.response.remarks = remarks;
                        this.alert_type = "Success";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    } else {
                        this.alert_type = "Warning";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    }
                },
                (error) => {
                    this.saving = false;
                    this.alert_type = "Error";
                    this.alert_message =
                        (error.response && error.response.data && error.response.data.message) ||
                        error.message ||
                        error.toString();
                    this.$refs.alert_model.openModel();
                }
            );
        },
        downloadFile(fileType) {
            UserService.authPostRequest(
                "download-qhls-faculty-meet-file",
                { meeting_id: this.meeting.id, file_type: fileType },
                { responseType: "blob" }
            ).then(
                (response) => {
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = this.meeting.name + "-" + fileType;
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    }, 100);
                },
                () => {
                    this.alert_type = "Error";
                    this.alert_message = "Unable to download file.";
                    this.$refs.alert_model.openModel();
                }
            );
        },
    },
};
</script>

<style scoped>
.fm-card {
    background: #fff;
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.fm-hero-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.fm-hero-icon {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #f5f3ff;
    color: #6d28d9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.fm-hero-info {
    flex: 1;
    min-width: 0;
}

.fm-hero-title {
    font-size: 0.92rem;
    font-weight: 700;
    color: #27173e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fm-hero-time {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fm-status {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 10px;
}

.fm-status-present {
    background: #dcfce7;
    color: #15803d;
}

.fm-status-leave {
    background: #fee2e2;
    color: #b91c1c;
}

.fm-status-pending {
    background: #fef3c7;
    color: #b45309;
}

.fm-reason-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f3f4f8;
    font-size: 0.78rem;
    color: #4b5563;
}

.fm-reason-row ion-icon {
    font-size: 14px;
    color: #6d28d9;
    flex-shrink: 0;
    margin-top: 1px;
}

.fm-download-row {
    display: flex;
    gap: 8px;
}

.fm-download-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 9px;
    border-radius: 8px;
    border: 1.5px solid #6d28d9;
    background: #f5f3ff;
    color: #6d28d9;
    font-weight: 600;
    font-size: 12px;
}

.fm-actions {
    display: flex;
    gap: 8px;
}

.fm-action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 11px;
    border-radius: 8px;
    border: none;
    font-weight: 700;
    font-size: 13px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.fm-present-btn {
    background: #16a34a;
    color: #fff;
}

.fm-leave-btn {
    background: #dc2626;
    color: #fff;
}

.fm-leave-card {
    margin-top: 8px;
}

.fm-textarea {
    resize: vertical;
    font-size: 0.85rem;
}

.fm-submit-leave-btn {
    width: 100%;
    margin-top: 8px;
    padding: 10px;
    border: none;
    border-radius: 8px;
    background: #dc2626;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
}

.fm-responded-note {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    font-size: 0.82rem;
    color: #4b5563;
}

.fm-responded-note ion-icon {
    font-size: 20px;
    color: #16a34a;
    flex-shrink: 0;
}
</style>
